import {
  AppWindow,
  ArrowRightLeft,
  BadgeDollarSign,
  Ban,
  Bot,
  Building2,
  CircleCheck,
  CircleSlash,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileSearch,
  FileWarning,
  Flame,
  Globe,
  IdCard,
  Inbox,
  Link2,
  LogIn,
  type LucideIcon,
  Mail,
  MailCheck,
  MailOpen,
  MailPlus,
  Merge,
  MessageSquare,
  MessageSquareReply,
  MessageSquareText,
  Package,
  PartyPopper,
  Pencil,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Plug,
  ReceiptText,
  RefreshCcw,
  RefreshCw,
  Send,
  SendHorizontal,
  ShieldCheck,
  ShieldX,
  ShoppingCart,
  Signature,
  Sparkles,
  StickyNote,
  TriangleAlert,
  UserCheck,
  UserPlus,
} from 'lucide-react'

import { SalesLeadActivityType } from '../../../../../models/salesLead'

interface ActivityTypeStyle {
  label: string
  icon: LucideIcon
  className: string
  /** A customer timeline event, whose notes are Markdown. */
  event?: boolean
}

const GREEN = 'bg-green-50 text-green-700 border-green-200'
const RED = 'bg-red-50 text-red-700 border-red-200'
const AMBER = 'bg-amber-50 text-amber-700 border-amber-200'
const ORANGE = 'bg-orange-50 text-orange-700 border-orange-200'
const BLUE = 'bg-blue-50 text-blue-700 border-blue-200'
const SKY = 'bg-sky-50 text-sky-700 border-sky-200'
const INDIGO = 'bg-indigo-50 text-indigo-700 border-indigo-200'
const NEUTRAL = 'bg-neutral-100 text-neutral-700 border-neutral-200'

function event(label: string, icon: LucideIcon, className: string): ActivityTypeStyle {
  return { label, icon, className, event: true }
}

export const activityTypeStyles: Record<SalesLeadActivityType, ActivityTypeStyle> = {
  [SalesLeadActivityType.CALL]: {
    label: 'Call',
    icon: Phone,
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [SalesLeadActivityType.CALL_INBOUND]: {
    label: 'Call · in',
    icon: PhoneIncoming,
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [SalesLeadActivityType.CALL_OUTBOUND]: {
    label: 'Call · out',
    icon: PhoneOutgoing,
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [SalesLeadActivityType.CALL_INBOUND_MISSED]: {
    label: 'Missed · in',
    icon: PhoneMissed,
    className: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  [SalesLeadActivityType.CALL_OUTBOUND_MISSED]: {
    label: 'Missed · out',
    icon: PhoneMissed,
    className: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  [SalesLeadActivityType.EMAIL]: {
    label: 'Email',
    icon: Mail,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  [SalesLeadActivityType.EMAIL_INBOUND]: {
    label: 'Email · in',
    icon: MailOpen,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  [SalesLeadActivityType.EMAIL_OUTBOUND]: {
    label: 'Email · out',
    icon: Send,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  [SalesLeadActivityType.EMAIL_OUTBOUND_DRAFT]: {
    label: 'AI Email Draft',
    icon: Sparkles,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  [SalesLeadActivityType.SMS]: {
    label: 'SMS',
    icon: MessageSquare,
    className: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  [SalesLeadActivityType.SMS_INBOUND]: {
    label: 'SMS · in',
    icon: Inbox,
    className: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  [SalesLeadActivityType.SMS_OUTBOUND]: {
    label: 'SMS · out',
    icon: MessageSquareReply,
    className: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  [SalesLeadActivityType.SMS_OUTBOUND_DRAFT]: {
    label: 'AI SMS Draft',
    icon: Sparkles,
    className: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  [SalesLeadActivityType.NOTE]: {
    label: 'Note',
    icon: StickyNote,
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  [SalesLeadActivityType.STATUS_CHANGE]: {
    label: 'Status Change',
    icon: ArrowRightLeft,
    className: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  },
  [SalesLeadActivityType.FIELD_UPDATE]: {
    label: 'Edit',
    icon: Pencil,
    className: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  },
  [SalesLeadActivityType.AI_SUMMARY]: {
    label: 'AI Summary',
    icon: Sparkles,
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  [SalesLeadActivityType.CARRIER_ELIGIBLE]: {
    label: 'Eligible',
    icon: ShieldCheck,
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [SalesLeadActivityType.CARRIER_INELIGIBLE]: {
    label: 'Ineligible',
    icon: ShieldX,
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  [SalesLeadActivityType.WEB_DOT_ENTRY]: event('Web DOT entry', Globe, SKY),
  [SalesLeadActivityType.SIGNED_UP]: event('Signed up', UserPlus, GREEN),
  [SalesLeadActivityType.SIGNED_IN]: event('Signed in', LogIn, NEUTRAL),
  [SalesLeadActivityType.APP_OPENED]: event('Opened the app', AppWindow, NEUTRAL),
  [SalesLeadActivityType.EMAIL_VERIFIED]: event('Email verified', MailCheck, GREEN),
  [SalesLeadActivityType.USER_INVITED]: event('User invited', UserPlus, INDIGO),
  [SalesLeadActivityType.USER_JOINED]: event('User joined', UserCheck, GREEN),
  [SalesLeadActivityType.ACCOUNT_LINKED]: event('Account linked', Link2, NEUTRAL),
  [SalesLeadActivityType.DUPLICATE_BLOCKED]: event('Duplicate blocked', Ban, RED),
  [SalesLeadActivityType.ORGS_MERGED]: event('Accounts merged', Merge, NEUTRAL),
  [SalesLeadActivityType.FUNNEL_NEEDS_REVIEW]: event('Needs review', TriangleAlert, ORANGE),
  [SalesLeadActivityType.APPLICATION_STARTED]: event('Application started', ClipboardList, INDIGO),
  [SalesLeadActivityType.APPLICATION_COMPLETED]: event('Application completed', ClipboardCheck, INDIGO),
  [SalesLeadActivityType.APPLICATION_SUBMITTED]: event('Application submitted', SendHorizontal, INDIGO),
  [SalesLeadActivityType.SENT_TO_CARRIERS]: event('Sent to carriers', SendHorizontal, INDIGO),
  [SalesLeadActivityType.REQUOTE_REQUESTED]: event('Re-quote requested', RefreshCcw, SKY),
  [SalesLeadActivityType.REQUOTED]: event('Re-quoted', RefreshCw, SKY),
  [SalesLeadActivityType.INDICATION_RECEIVED]: event('New indication', ReceiptText, GREEN),
  [SalesLeadActivityType.INDICATION_FAILED]: event('Indication failed', TriangleAlert, RED),
  [SalesLeadActivityType.NO_CARRIER_QUOTED]: event('No carrier quoted', CircleSlash, RED),
  [SalesLeadActivityType.QUOTES_SENT]: event('Quotes sent', MailPlus, BLUE),
  [SalesLeadActivityType.QUOTES_COMPARED]: event('Comparing quotes', Eye, AMBER),
  [SalesLeadActivityType.QUOTE_SHORTLISTED]: event('Shortlisted', Flame, ORANGE),
  [SalesLeadActivityType.INDICATIONS_SELECTED]: event('Quotes picked', CircleCheck, GREEN),
  [SalesLeadActivityType.QUOTE_READY]: event('Quote ready', BadgeDollarSign, GREEN),
  [SalesLeadActivityType.QUOTE_SELECTED]: event('Quote selected', ShoppingCart, GREEN),
  [SalesLeadActivityType.POLICY_BOUND]: event('Policy bound', PartyPopper, GREEN),
  [SalesLeadActivityType.POLICY_CANCELED]: event('Policy canceled', Ban, RED),
  [SalesLeadActivityType.CARRIER_UPDATE]: event('Carrier update', Building2, BLUE),
  [SalesLeadActivityType.CARRIER_ISSUE]: event('Carrier issue', TriangleAlert, RED),
  [SalesLeadActivityType.DOCUMENT_SIGNED]: event('Signed', Signature, GREEN),
  [SalesLeadActivityType.LOSS_RUN_UPDATE]: event('Loss runs', FileSearch, AMBER),
  [SalesLeadActivityType.LARGE_FLEET_PACKET]: event('Large-fleet packet', Package, INDIGO),
  [SalesLeadActivityType.MVR_COMPLETED]: event('MVR pulled', IdCard, NEUTRAL),
  [SalesLeadActivityType.ELD_CONNECTED]: event('ELD connected', Plug, GREEN),
  [SalesLeadActivityType.BROKER_NOTE]: event('Note to broker', MessageSquareText, AMBER),
  [SalesLeadActivityType.CLAIM_REPORTED]: event('Phone claim', PhoneCall, RED),
  [SalesLeadActivityType.CLAIM_SUBMITTED]: event('Claim submitted', FileWarning, RED),
  [SalesLeadActivityType.ENDORSEMENT_SUBMITTED]: event('Policy change requested', RefreshCw, AMBER),
  [SalesLeadActivityType.ENDORSEMENT_UPDATED]: event('Policy change updated', RefreshCw, AMBER),
  [SalesLeadActivityType.ASKED_CAI]: event('Asked Cai', Bot, SKY),
}
